"use client";

import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FaPrint } from "react-icons/fa";
import { AddressInput } from "@/components/ui";
import Button from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SmartDateInput } from "@/components/ui/smart-date-input";
import Textarea from "@/components/ui/textarea";
import { useTenant } from "@/contexts/tenant-provider";
import { pickedDay } from "@/lib/clinic-clock";
import { getDoseComponents } from "@/lib/dose-components";
import { formatDate, formatPhoneNumber, formatStaffName, fromDateOnlyString, toClinicDateKey, toUtcDateKey } from "@/lib/format-data";
import { resolveDoseLabelLotMetadata } from "@/lib/medication-label";
import { MedicationLabelData, buildInitialLabelData, buildLabelPrintDocument, formatAddressLine } from "@/lib/medication-label";
import { writeToPrintWindow } from "@/lib/print-utils";
import { nameShowsCredentials } from "@/lib/staff-name-display";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { MedicationSummary } from "@/types/medication";
import { PatientSummary } from "@/types/patient";
import LabelPreview from "./label-preview";
import { providerQueryOptions } from "@/api/provider/admin/provider-query";
import { useSaveDoseLabelMutation } from "@/api/provider/user/patient/patient-mutation";
import { medicationsQueryOptions } from "@/query/medications/medications";
import { locationQueryOptions } from "@/query/settings/location";
interface PrintLabelProps {
  medication: MedicationSummary;
  patient: PatientSummary;
  children: React.ReactNode;
}
type FieldKey = keyof MedicationLabelData;
interface FieldDef {
  key: FieldKey;
  label: string;
  span2?: boolean;
}
type DateKey = "dispDate" | "rxDate" | "exp";

// Fields grouped into sections for a cleaner form layout.
// Clinic address + phone are driven by the location dropdown, not free text.
const CLINIC_FIELDS: FieldDef[] = [{
  key: "clinicName",
  label: "Clinic / Prescriber",
  span2: true
}, {
  key: "npi",
  label: "NPI #"
}, {
  key: "dea",
  label: "DEA #"
}];
const PATIENT_FIELDS: FieldDef[] = [{
  key: "patientName",
  label: "Patient"
}, {
  key: "patientId",
  label: "Patient ID"
}, {
  key: "dob",
  label: "DOB"
}, {
  key: "patientPhone",
  label: "Patient phone"
}];
const DISPENSE_FIELDS: FieldDef[] = [{
  key: "disp",
  label: "Dispense"
}, {
  key: "refill",
  label: "Refills"
}, {
  key: "ndc",
  label: "NDC #"
}, {
  key: "lot",
  label: "Lot #"
}];
const DATE_FIELDS: {
  key: DateKey;
  label: string;
}[] = [{
  key: "dispDate",
  label: "Disp date"
}, {
  key: "rxDate",
  label: "Rx date"
}, {
  key: "exp",
  label: "Expiration"
}];
const EMPTY_ADDRESS = {
  address: "",
  addressLineTwo: "",
  city: "",
  state: "",
  zipCode: ""
};

/**
 * The UTC calendar day of a wire value, as a local Date the pickers can hold.
 * Returns undefined rather than throwing on anything Date cannot parse — this
 * runs while the dialog is seeding, so a throw would take the dialog down.
 */
function utcDayOf(value: string | Date | null | undefined): Date | undefined {
  if (!value) return undefined;
  const key = toUtcDateKey(value);
  return key ? fromDateOnlyString(key) : undefined;
}
export default function PrintLabel({
  medication,
  patient,
  children
}: PrintLabelProps) {
  const {
    tenant
  } = useTenant();
  const {
    toast
  } = useToast();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<MedicationLabelData>(() => buildInitialLabelData(medication, patient, tenant));
  const [selectedProviderId, setSelectedProviderId] = useState<string>("");
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [dateValues, setDateValues] = useState<Partial<Record<DateKey, Date | undefined>>>({});
  const saveDoseLabel = useSaveDoseLabelMutation();
  // Ref guards the seed effect from re-running; state drives the disabled Print
  // button so staff can't print a half-populated label before prefill lands.
  const seededRef = useRef(false);
  const [seeded, setSeeded] = useState(false);
  const [pendingAction, setPendingAction] = useState<"save" | "print" | null>(null);

  // Shipping address gets the shared Google-autocomplete address input, so it
  // lives in its own react-hook-form context.
  const addressForm = useForm({
    defaultValues: EMPTY_ADDRESS
  });

  // The component that carries the lot — its medication (catalog item) id is
  // what the available-lots endpoint keys on (NOT the dose id).
  const lotComponents = getDoseComponents(medication).filter(c => c.lots?.some(lot => lot.shipmentId));

  // Only fetch once the dialog is open.
  const providersQuery = useQuery({
    ...providerQueryOptions.list(),
    enabled: open
  });
  const locationsQuery = useQuery({
    ...locationQueryOptions.list(),
    enabled: open
  });
  const lotsQueries = useQueries({
    queries: lotComponents.map(component => ({
      ...medicationsQueryOptions.availableLots(component.medicationId, medication.locationId, undefined, undefined, true),
      enabled: open && !!component.medicationId
    }))
  });
  const providers = providersQuery.data ?? [];
  const locations = locationsQuery.data?.data ?? [];
  const lots = lotsQueries.flatMap(query => query.data ?? []);
  const adminProvider = providers.find(p => p.id === medication.providerId);
  const formatPrescriber = (p?: {
    firstName: string;
    lastName?: string;
    credentials?: string;
  }) => p ? [formatStaffName(p),
  // Already in the name when the prescriber chose to show them there.
  nameShowsCredentials(p) ? null : p.credentials].filter(Boolean).join(" ") : "";

  // Address = where the dose was administered (a property of the dose, not the
  // prescriber). Older doses have no location, so fall back to the administering
  // provider's primary location, then the sole clinic.
  const resolvedLocation = locations.find(l => l.id === medication.locationId) ?? (adminProvider?.primaryLocationId ? locations.find(l => l.id === adminProvider.primaryLocationId) : undefined) ?? (locations.length === 1 ? locations[0] : null);

  // The location driving the clinic address/phone/name — the dropdown pick,
  // falling back to the auto-resolved administration location.
  const selectedLocation = locations.find(l => l.id === selectedLocationId) ?? resolvedLocation;

  // Header line 1: prescriber, then tenant name, then location name. Only the
  // prescriber part changes when a different prescriber is picked.
  const buildClinicLine = (prescriber: string) => [prescriber, tenant?.name, selectedLocation?.name].filter(Boolean).join("  ");
  const {
    ndc: resolvedNdc,
    expiryDate: earliestExpiry
  } = resolveDoseLabelLotMetadata(medication, lots);
  const lookupsSettled = providersQuery.isFetched && locationsQuery.isFetched && lotsQueries.every(query => query.isFetched);

  // Seed once per open, after lookups settle, so pre-filled DEA / address / NDC
  // / expiration land without clobbering edits.
  useEffect(() => {
    if (!open || !lookupsSettled || seededRef.current) return;
    const base = buildInitialLabelData(medication, patient, tenant, {
      prescriber: adminProvider ? formatPrescriber(adminProvider) : undefined,
      npi: adminProvider?.nationalProviderIdentifier,
      dea: adminProvider?.deaNumber,
      location: resolvedLocation,
      ndc: resolvedNdc
    });
    // Reprint: if a label was already saved for this dose, seed from that
    // snapshot so we reproduce exactly what was printed.
    const snapshot = medication.labelData;
    setData(snapshot ? {
      ...base,
      ...snapshot
    } : base);
    setSelectedProviderId(adminProvider?.id ?? "");
    // On reprint, match the dropdown to the location whose address the snapshot
    // was printed with (so the dropdown doesn't contradict the shown address);
    // otherwise default to the auto-resolved administration location.
    const snapshotLocation = snapshot?.clinicAddress ? locations.find(l => formatAddressLine({
      address: l.addressLineOne,
      addressLineTwo: l.addressLineTwo,
      city: l.city,
      state: l.state,
      zipCode: l.zipCode
    }) === snapshot.clinicAddress) : undefined;
    setSelectedLocationId(snapshot ? snapshotLocation?.id ?? "" : resolvedLocation?.id ?? "");
    setDateValues(snapshot ? {
      dispDate: snapshot.dispDate ? new Date(snapshot.dispDate) : undefined,
      rxDate: snapshot.rxDate ? new Date(snapshot.rxDate) : undefined,
      exp: snapshot.exp ? new Date(snapshot.exp) : undefined
    } : {
      // Seed each field as the calendar day it should print, so picked
      // and seeded values mean the same thing downstream. Which day that
      // is differs by field:
      //
      // dispDate and rxDate are instants, so it is the day in the
      // viewer's zone — toClinicDateKey, not the browser's components.
      //
      // exp is a calendar day already: shipment.expiry_date is 30.6%
      // exactly 00:00:00Z in production and the rest US tenant-local
      // midnight, so its day is the UTC one and reading it locally would
      // drop it west of where it was written.
      dispDate: fromDateOnlyString(toClinicDateKey(new Date())),
      rxDate: fromDateOnlyString(toClinicDateKey(medication.doseDate ?? medication.createdDate)),
      exp: utcDayOf(earliestExpiry)
    });
    // On reprint, leave the address form empty so the shipping-sync effect
    // doesn't overwrite the snapshot's saved shipping line with the patient's
    // current address — keeping the reprint an exact reproduction.
    if (!snapshot) {
      addressForm.reset({
        address: patient.address ?? "",
        addressLineTwo: patient.addressLineTwo ?? "",
        city: patient.city ?? "",
        state: patient.state ?? "",
        zipCode: patient.zipCode ?? ""
      });
    }
    seededRef.current = true;
    setSeeded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lookupsSettled]);

  // Keep the label's date strings in sync with the date pickers.
  useEffect(() => {
    setData(prev => ({
      ...prev,
      // Every value in dateValues is now a local Date whose own calendar
      // components are the day meant to print — seeded that way above, or
      // picked that way by the user — so all three reduce identically.
      dispDate: dateValues.dispDate ? formatDate(pickedDay(dateValues.dispDate)) : "",
      rxDate: dateValues.rxDate ? formatDate(pickedDay(dateValues.rxDate)) : "",
      exp: dateValues.exp ? formatDate(pickedDay(dateValues.exp)) : ""
    }));
  }, [dateValues]);

  // Keep the label's shipping line in sync with the address input.
  const ship = addressForm.watch();
  useEffect(() => {
    setData(prev => ({
      ...prev,
      shippingAddress: formatAddressLine(ship)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ship.address, ship.addressLineTwo, ship.city, ship.state, ship.zipCode]);
  const handleOpenChange = (next: boolean) => {
    if (next) {
      seededRef.current = false;
      setSeeded(false);
      setSelectedProviderId("");
      setSelectedLocationId("");
      setDateValues({});
      addressForm.reset(EMPTY_ADDRESS);
      setData(buildInitialLabelData(medication, patient, tenant));
    }
    setOpen(next);
  };
  const setField = (key: FieldKey, value: string) => setData(prev => ({
    ...prev,
    [key]: value
  }));

  // The prescriber name currently reflected on the header line, so location
  // picks preserve it (and vice versa).
  const currentPrescriberName = () => selectedProviderId ? formatPrescriber(providers.find(p => p.id === selectedProviderId)) : adminProvider ? formatPrescriber(adminProvider) : "";
  const handlePickPrescriber = (providerId: string) => {
    setSelectedProviderId(providerId);
    const picked = providers.find(p => p.id === providerId);
    setData(prev => ({
      ...prev,
      npi: picked?.nationalProviderIdentifier ?? "",
      dea: picked?.deaNumber ?? "",
      clinicName: buildClinicLine(formatPrescriber(picked))
    }));
  };

  // Picking a location drives the clinic address, phone, and the location name
  // on the header line.
  const handlePickLocation = (locationId: string) => {
    setSelectedLocationId(locationId);
    const loc = locations.find(l => l.id === locationId);
    const phone = loc?.phoneNumber || tenant?.phoneNumber || "";
    setData(prev => ({
      ...prev,
      clinicName: [currentPrescriberName(), tenant?.name, loc?.name].filter(Boolean).join("  "),
      clinicAddress: loc ? formatAddressLine({
        address: loc.addressLineOne,
        addressLineTwo: loc.addressLineTwo,
        city: loc.city,
        state: loc.state,
        zipCode: loc.zipCode
      }) : "",
      clinicPhone: phone ? formatPhoneNumber(phone) : ""
    }));
  };
  const persistLabel = async (action: "save" | "print") => {
    await saveDoseLabel.mutateAsync({
      doseId: medication.id,
      labelData: data,
      action
    });
    setOpen(false);
  };
  const handleSave = async () => {
    setPendingAction("save");
    try {
      await persistLabel("save");
    } catch {
      // The mutation displays the error and rolls back the optimistic update.
    } finally {
      setPendingAction(null);
    }
  };
  const handlePrint = async () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast({
        title: "Could not print",
        description: "Unable to open print window. Please check your browser settings."
      });
      return;
    }
    const printDocument = buildLabelPrintDocument(data);
    setPendingAction("print");
    try {
      await persistLabel("print");
      writeToPrintWindow(printWindow, printDocument);
    } catch {
      printWindow.close();
      // The mutation reports that the label failed to save; nothing is printed.
    } finally {
      setPendingAction(null);
    }
  };
  const renderField = ({
    key,
    label,
    span2
  }: FieldDef) => <div key={key} className={cx("flex flex-col gap-1.5", span2 && "sm:col-span-2")}>
      <Label htmlFor={`label-${key}`} className="text-muted-foreground">
        {label}
      </Label>
      <Input id={`label-${key}`} value={data[key]} onChange={e => setField(key, e.target.value)} />
    </div>;
  const renderDate = ({
    key,
    label
  }: {
    key: DateKey;
    label: string;
  }) =>
  // Full width (own row) — the date input's calendar/clear controls are wide.
  <div key={key} className="flex flex-col gap-1.5 sm:col-span-2">
      <Label className="text-muted-foreground">{label}</Label>
      <SmartDateInput placeholder="Select date" direction={key === "exp" ? "future" : "past"} value={dateValues[key]} onChange={d => setDateValues(prev => ({
      ...prev,
      [key]: d
    }))} />
    </div>;
  const sectionHeading = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";
  return <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Medication Label</DialogTitle>
          <DialogDescription>
            Review and edit the label, then save it for later or print it now.
            Fields are pre-filled where we have the data.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col gap-8 lg:flex-row">
            {/* Left: prescriber, patient, dispensing. */}
            <div className="flex min-w-0 flex-1 flex-col gap-6">
              <section className="flex flex-col gap-3">
                <h3 className={sectionHeading}>Prescriber &amp; clinic</h3>
                <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <Label htmlFor="label-prescriber" className="text-muted-foreground">
                      Prescriber
                    </Label>
                    <Select value={selectedProviderId} onValueChange={handlePickPrescriber}>
                      <SelectTrigger id="label-prescriber">
                        <SelectValue placeholder="Select a prescriber" />
                      </SelectTrigger>
                      <SelectContent>
                        {providers.map(p => <SelectItem key={p.id} value={p.id}>
                            {formatStaffName(p)}
                            <span>
                              {p.nationalProviderIdentifier ? ` — NPI ${p.nationalProviderIdentifier}` : p.deaNumber ? ` — DEA ${p.deaNumber}` : ""}
                            </span>
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {CLINIC_FIELDS.map(renderField)}
                  <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <Label htmlFor="label-location" className="text-muted-foreground">
                      Clinic location
                    </Label>
                    <Select value={selectedLocationId} onValueChange={handlePickLocation}>
                      <SelectTrigger id="label-location">
                        <SelectValue placeholder="Select a location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map(l => <SelectItem key={l.id} value={l.id}>
                            {l.name}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </section>

              <section className="flex flex-col gap-3">
                <h3 className={sectionHeading}>Patient</h3>
                <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                  {PATIENT_FIELDS.map(renderField)}
                </div>
                <FormProvider {...addressForm}>
                  <AddressInput label="Shipping address" defaultExpanded />
                </FormProvider>
              </section>

              <section className="flex flex-col gap-3">
                <h3 className={sectionHeading}>Dispensing</h3>
                <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                  {DISPENSE_FIELDS.map(renderField)}
                  {DATE_FIELDS.map(renderDate)}
                </div>
              </section>
            </div>

            {/* Right: preview + medication & directions (so the form doesn't scroll). */}
            <div className="flex shrink-0 flex-col gap-4 lg:sticky lg:top-0 lg:self-start">
              <div className="flex flex-col gap-2">
                <h3 className={sectionHeading}>Label preview</h3>
                <div className="flex justify-center rounded-xl border bg-muted/30 p-6">
                  <LabelPreview data={data} scale={1.7} />
                </div>
                <p className="text-xs text-muted-foreground">
                  Actual size 3.5&quot; × 1.125&quot; · text auto-fits to the
                  label
                </p>
              </div>

              <section className="flex flex-col gap-3">
                <h3 className={sectionHeading}>Medication &amp; directions</h3>
                {renderField({
                key: "medication",
                label: "Medication"
              })}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="label-sig" className="text-muted-foreground">
                    Directions (Sig)
                  </Label>
                  <Textarea id="label-sig" rows={3} value={data.sig} onChange={e => setField("sig", e.target.value)} />
                </div>
              </section>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={handleSave} disabled={!seeded || saveDoseLabel.isPending} isLoading={pendingAction === "save"} loadingText="Saving...">
            Save Label
          </Button>
          <Button variant="primary" onClick={handlePrint} disabled={!seeded || saveDoseLabel.isPending} isLoading={pendingAction === "print"} loadingText="Printing..." className="flex items-center gap-2">
            <FaPrint className="h-4 w-4" />
            Print Label
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}